/**
 * What the idle game, the server and the archived-world view share with the old game (D317):
 * the hash and RNG behind map seeds, the map seed rules (D41), the calendar's hour
 * alignment (D98), the weather names, and the wire types every live route answers with.
 * Nothing here reads a clock or the old game's state.
 */
import type { IdleRefusal, IdleWelcomeBack } from './idle/world.ts';
import type { IdleWorldState, IdleRegionBonus } from './idle/state.ts';

// ---------------------------------------------------------------- hashing and RNG

/** FNV-1a 32 bit hash of a string. */
export function hash32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32. Returns a function giving floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** An RNG for one named roll, for example `rng(worldSeed, 'weather', dayIndex)`. */
export function rng(...parts: (string | number)[]): () => number {
  return mulberry32(hash32(parts.join('|')));
}

// ---------------------------------------------------------------- map seeds (D41)

/** Longest a player-chosen map seed may be. */
export const MAP_SEED_MAX = 32;

/**
 * The words `randomMapSeed` builds from. Plain hedgerow words, so a seed reads as
 * something a player can say out loud and type back in: "quiet-orchard-42".
 */
export const MAP_SEED_WORDS = [
  'alder',
  'barley',
  'bramble',
  'briar',
  'clover',
  'coppice',
  'dovecote',
  'elm',
  'fallow',
  'fern',
  'foxglove',
  'granary',
  'hawthorn',
  'hazel',
  'hollow',
  'ivy',
  'kiln',
  'larch',
  'meadow',
  'nettle',
  'oak',
  'orchard',
  'quarry',
  'quiet',
  'rushy',
  'sedge',
  'stone',
  'thistle',
  'willow',
  'yew',
];

/**
 * A map seed as the game stores and compares it: trimmed, runs of spaces collapsed to
 * one, lower-cased. Two players who type the same seed differently get the same map.
 */
export function normaliseMapSeed(input: string): string {
  return input.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** True for 1 to 32 characters of letters, digits, spaces and dashes (D41). */
export function isMapSeed(input: string): boolean {
  return new RegExp(`^[a-z0-9 -]{1,${MAP_SEED_MAX}}$`).test(input);
}

/** A readable random map seed: two words and a number, from `rngKey`. */
export function randomMapSeed(rngKey: string): string {
  const next = rng(rngKey, 'map-seed');
  const word = () => MAP_SEED_WORDS[Math.floor(next() * MAP_SEED_WORDS.length)] as string;
  return `${word()}-${word()}-${Math.floor(next() * 90) + 10}`;
}

// ---------------------------------------------------------------- calendar and weather

const ONE_HOUR_MS = 3_600_000;

/**
 * The calendar start, floored to a whole hour. D98: the day's hours and the world's hourly
 * ticks only agree if the start sits on an hour, so it is aligned where it is set and
 * floored again here, which is what carries an older world whose start was not on the hour.
 */
export function alignCalendarStart(ms: number): number {
  return Math.floor(ms / ONE_HOUR_MS) * ONE_HOUR_MS;
}

/** The hour of the in-game day, 0 to 23, counted from the aligned calendar start. D98. */
export function hourOfDay(ms: number, calendarStartMs: number): number {
  const since = Math.max(0, ms - alignCalendarStart(calendarStartMs));
  return Math.floor((since % (24 * ONE_HOUR_MS)) / ONE_HOUR_MS);
}

export type Weather = 'fair' | 'rain' | 'dry' | 'storm' | 'frost';

// ---------------------------------------------------------------- the wire (D240 to D246)
//
//   GET  /world?open=1         -> IdleWorldResponse. Advances to server time. With open=1 it
//                                 also marks the player as here and carries `welcomeBack`
//                                 (computed before the check-in).
//   GET  /world                -> the same without `welcomeBack`: the sync poll.
//   POST /world                CreateWorldRequest (only `name`, `mapSeed` and `theme` are read)
//                                 -> 201 IdleCreateResponse
//   POST /world/act            IdleIntentEnvelope -> IdleActResponse, one result per intent.
//                                 An archived world answers 409 ArchivedRefusal.
//   GET  /worlds               -> WorldsResponse; each WorldSummary carries `kind` and `archived`.
//   GET  /worlds/:id/view      -> WorldViewResponse: any of the caller's worlds as stored, not
//                                 advanced, so an archived world can still be looked at.
//   POST /worlds/:id/select    -> makes one of the caller's idle worlds the current one (D337);
//                                 refused with 409 ArchivedRefusal for an archived world.
//   GET  /region/:id           -> { ok: true, region: IdleRegionPublic }: the weekly goal and
//                                 the table of the region's worlds.

/** The idle game or the old one (D241). Every classic world is archived. */
export type WorldKind = 'classic' | 'idle';

export interface ErrorResponse {
  ok: false;
  error: string;
  /** Present on 429. */
  retryAfterMs?: number;
}

/**
 * POST /world refused (D337): `world limit` when the player already keeps
 * `worldsPerPlayer` worlds, `name taken` when another of their worlds has the name. `reason` is
 * the words to show.
 */
export interface CreateWorldRefusal {
  ok: false;
  error: 'world limit' | 'name taken';
  reason: string;
  worlds?: WorldSummary[];
}

/** Why an action on an archived world was refused, in words the screen can show as they are. */
export interface ArchivedRefusal {
  ok: false;
  error: 'archived';
  reason: string;
}

/** POST /world. The server makes the state (D1); the client sends only what the player chose. */
export interface CreateWorldRequest {
  name: string;
  /** The old game's onboarding crop. Not read by the server any more. */
  startCrop?: string;
  /** The map seed typed or shuffled (D41, D267). Empty or missing means none. */
  mapSeed?: string;
  /**
   * D348, D350: the theme the world is made in, by id (`idleThemeIds()`). Missing means the world
   * theme. Set once here and never changed: no route takes it again.
   */
  theme?: string;
}

/** One of the account's active worlds, as `GET /worlds` lists them (D40). */
export interface WorldSummary {
  worldId: string;
  name: string;
  /** 1 to 3. */
  slot: number;
  stage: number;
  worldValue: number;
  regionId: string;
  /** The public map seed (D41). Absent on a world whose layout came from the seed alone. */
  mapSeed?: string;
  /** D344: the world's kind and land traits by id, its kind first. Absent before kinds. */
  mapTraits?: string[];
  /** D348: the world's theme, by id. Fixed when the world is made (D350). */
  theme: string;
  /** The world's Heritage (D221), so a delete can say what it loses. Absent when none. */
  heritage?: number;
  createdAt: number;
  kind: WorldKind;
  /** An old-game world kept to look at, never played (D241). It takes no slot. */
  archived: boolean;
  /** An idle world's coins earned across every year. */
  lifetimeEarned?: number;
}

/** GET /worlds. Up to `worldsPerPlayer` worlds and which one the session is acting on. */
export interface WorldsResponse {
  ok: true;
  worlds: WorldSummary[];
  currentWorldId: string;
}

/**
 * POST /worlds/:id/delete (D221, D337). Any of the caller's worlds, the open one included: the
 * session then moves to another playable world, or to none (the first run). A refusal carries the reason and what would lift it
 * (D180); a delete that stopped part way names what is still there, and a retry finishes it.
 */
export type DeleteWorldResponse =
  /** `currentWorldId`: the world open after the delete, or '' when none can be played (D337). */
  | { ok: true; worldId: string; worlds: WorldSummary[]; currentWorldId: string }
  | { ok: false; error: string; reason?: string; left?: string[] };

/**
 * POST /worlds/:id/rename (D312). The same name rules as a new world (D267): not empty, not
 * over length, and not another of the caller's own worlds' names. A refusal carries the
 * reason to show.
 */
export type RenameWorldResponse =
  { ok: true; worldId: string; name: string } | { ok: false; error: string; reason?: string };

/** The region's weekly goal as a world sees it (D244). */
export interface IdleRegionGoal {
  /** Week number since the world's first Monday. */
  week: number;
  weekStartsAt: number;
  weekEndsAt: number;
  /** Coins the region's worlds have earned this week. */
  earned: number;
  target: number;
  /** True once `earned` has reached `target` this week. It stays true for the week. */
  met: boolean;
  /** Idle worlds counted towards the goal. */
  members: number;
  /** The value multiplier every member gets next week when this week's goal is met. */
  factor: number;
  /** This world's own earnings counted towards this week's goal (D262). */
  mine?: number;
}

/** The region part of every idle world response. `bonus` is in force when present. */
export interface IdleRegionView {
  regionId: string;
  name: string;
  goal: IdleRegionGoal;
  /** This week's reward for last week's goal, the same as `state.region`. */
  bonus?: IdleRegionBonus;
}

/** One row of the region's table of idle worlds, most earned this week first. */
export interface IdleRegionWorld {
  worldId: string;
  name: string;
  /** Every coin the world has earned, across every year. */
  lifetimeEarned: number;
  /** Coins it has counted towards this week's goal: the table's "This week" (D262). */
  weekEarned: number;
  /** Heritage earned at festivals, which only rises (D246). */
  heritage: number;
  /** D433: the world's theme id, so the table can show its own tab icon. */
  theme: string;
  /** True for the caller's own world. */
  mine?: boolean;
}

/** GET /region/:id for an idle world. */
export interface IdleRegionPublic extends IdleRegionView {
  worlds: IdleRegionWorld[];
}

/** GET /world on an idle world. */
export interface IdleWorldResponse {
  ok: true;
  kind: 'idle';
  /** D348: the world's theme; its content is `getIdleContent(theme)`. */
  theme: string;
  version: number;
  serverTime: number;
  worldId: string;
  name: string;
  state: IdleWorldState;
  region: IdleRegionView;
  /** Only with `open=1`: what the barn banked while the player was away. */
  welcomeBack?: IdleWelcomeBack;
}

/** POST /world. */
export interface IdleCreateResponse {
  ok: true;
  kind: 'idle';
  /** D348: the theme the world was made in; its content is `getIdleContent(theme)`. */
  theme: string;
  worldId: string;
  version: number;
  state: IdleWorldState;
}

/** One result per intent, in order. `needs` is the coin shortfall on `not_enough_coins`. */
export type IdleActResult = { ok: true } | { ok: false; reason: IdleRefusal; needs?: number };

/** POST /world/act on an idle world. `state` is the world after every intent in the envelope. */
export interface IdleActResponse {
  ok: true;
  kind: 'idle';
  version: number;
  serverTime: number;
  state: IdleWorldState;
  region: IdleRegionView;
  results: IdleActResult[];
  /** True when the envelope id had been seen before and the stored outcome was returned. */
  replayed: boolean;
}

/**
 * What is kept of an old-game world to look at (D262, D317): read off its stored save, never
 * simulated. `lastSeenAt` is null when the save does not say.
 */
export interface ArchivedWorldState {
  stage: number;
  lastSeenAt: number | null;
}

/** GET /worlds/:id/view. The world as stored, never advanced. */
export type WorldViewResponse =
  | {
      ok: true;
      kind: 'idle';
      archived: boolean;
      worldId: string;
      name: string;
      state: IdleWorldState;
    }
  | {
      ok: true;
      kind: 'classic';
      archived: boolean;
      worldId: string;
      name: string;
      state: ArchivedWorldState;
    };

/**
 * An old-game save, as stored, reduced to what the archived view shows (D317). Anything it
 * cannot read is left out rather than guessed: a missing stage is 1, a missing time null.
 */
export function archivedWorldState(save: unknown): ArchivedWorldState | null {
  if (!save || typeof save !== 'object') return null;
  const s = save as { stage?: unknown; lastSeenAt?: unknown };
  return {
    stage: typeof s.stage === 'number' && Number.isFinite(s.stage) ? s.stage : 1,
    lastSeenAt:
      typeof s.lastSeenAt === 'number' && Number.isFinite(s.lastSeenAt) ? s.lastSeenAt : null,
  };
}
